// @flow

import { isEqual } from "lodash";
import * as React from "react";
import queryString from "query-string";
import NProgress from "nprogress";
import {
    withRouter,
    Switch,
    useHistory,
    Route,
    RouteComponentProps,
} from "react-router-dom";
import { useSelector } from "react-redux";

import API from "../../api/user";
import Config from "../../constants/Config";
import getContentReferenceTileProps from "../../utils/getContentReferenceTileProps";
import NavTabs from "../molecules/NavTabs";
import Loader from "../atoms/Loader";
import Tile from "../molecules/Tile";
import Pagination from "../molecules/Pagination";
import { useGetEventApplicationsByClubQuery } from "../../services/wp";
import { format, parse } from "date-fns";
import { StatusPill } from "../atoms/StatusPill";

interface PropsType extends RouteComponentProps {
    id: any;
}

export const EventList = ({ events, type, id, isLoading, managed = false }) => {
    return (
        <div className="row">
            <div className="col-xs-12">
                <div style={{ position: "relative" }}>
                    <Loader active={isLoading}>
                        {events && events.length > 0 ? (
                            events.map((event, index) => {
                                const referenceContent =
                                    getContentReferenceTileProps({
                                        ...event,
                                        post_type: "club_event",
                                        url: `${
                                            managed
                                                ? Config.routes.managedEvent
                                                : Config.routes.manageEvent
                                        }/${event.ID || event.id}`,
                                    });

                                return (
                                    <div
                                        key={index}
                                        className="club-profile-body__content__row row"
                                    >
                                        <div className="col-md-12">
                                            <Tile {...referenceContent} />
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="manage-clubs-events-body__empty">
                                The club does not have any {type} events.
                            </div>
                        )}
                    </Loader>
                </div>
            </div>
        </div>
    );
};

interface ApplicationListProps {
    id: number;
}

const ApplicationList = ({ id }: ApplicationListProps) => {
    const { data, isLoading, error } = useGetEventApplicationsByClubQuery(id);

    React.useEffect(() => {
        if (isLoading) {
            NProgress.start();
        } else {
            NProgress.done();
        }
    }, [isLoading]);

    return (
        <Loader active={isLoading}>
            {error ? (
                <div className="manage-clubs-events-body__empty">
                    An error has occurred. Please try again.
                </div>
            ) : data?.length === 0 ? (
                <div className="manage-clubs-events-body__empty">
                    The club does not have any event applications.
                </div>
            ) : (
                <div className="manage-clubs-events-body__list">
                    {data?.map((application, i) => {
                        const createdOn = application.created_on
                            ? parse(
                                  application.created_on,
                                  "yyyy-MM-dd H:mm:ss",
                                  new Date()
                              )
                            : null;
                        const eventDate = application.event_date
                            ? parse(
                                  application.event_date,
                                  "yyyy-MM-dd H:mm:ss",
                                  new Date()
                              )
                            : null;

                        return (
                            <React.Fragment key={i}>
                                <div className="manage-clubs-events-body__list__item__property manage-clubs-events-body__list__item__description">
                                    <div className="manage-clubs-events-body__list__item__description__name">
                                        {application.name}
                                    </div>
                                </div>
                                <div className="manage-clubs-events-body__list__item__property manage-clubs-events-body__list__item__property__applied">
                                    <div className="manage-clubs-events-body__list__item__label">
                                        Applied On
                                    </div>
                                    <div className="manage-clubs-events-body__list__item__value">
                                        {createdOn &&
                                        !isNaN(createdOn.valueOf())
                                            ? format(createdOn, "d LLL y")
                                            : null}
                                    </div>
                                </div>
                                <div className="manage-clubs-events-body__list__item__property">
                                    <div className="manage-clubs-events-body__list__item__label">
                                        Event date
                                    </div>
                                    <div className="manage-clubs-events-body__list__item__value">
                                        {eventDate &&
                                        !isNaN(eventDate.valueOf())
                                            ? format(eventDate, "d LLL y")
                                            : null}
                                    </div>
                                </div>
                                <div className="manage-clubs-events-body__list__item__property">
                                    <div className="manage-clubs-events-body__list__item__label">
                                        Location
                                    </div>
                                    <div className="manage-clubs-events-body__list__item__value">
                                        {application.location}
                                    </div>
                                </div>
                                <div className="manage-clubs-events-body__list__item__status">
                                    {application.status ? (
                                        <StatusPill
                                            status={application.status}
                                        />
                                    ) : null}
                                </div>
                                <div className="manage-clubs-events-body__list__row-divider" />
                            </React.Fragment>
                        );
                    })}
                </div>
            )}
        </Loader>
    );
};

const ManageClubsEventsBody = (props: PropsType) => {
    const POSTS_PER_PAGE = 12;
    const PAGINATION_WIDTH = 2;
    const history = useHistory();
    const { create_event_form_link } = useSelector(
        (state: any): any => state.options
    );

    const [clubEvents, setClubEvents] = React.useState<any[]>([]);
    const [headers, setHeaders] = React.useState({});
    const [isLoading, setIsLoading] = React.useState(true);
    const [params, setParams] = React.useState({ page_num: 1 });

    const queryParamProps = queryString.parse(history.location.search);
    const activePage = Number(queryParamProps.page_num) || 1;
    const totalPages = parseInt(headers["x-wp-totalpages"]) || 1;

    // set query params in state on change of any params
    if (!isEqual(queryParamProps, params)) {
        setParams(queryParamProps);
    }

    const tabs = [
        {
            to: `${Config.routes.manageClubs}/${props.id}/events`,
            title: "Upcoming",
            value: "upcoming",
        },
        {
            to: `${Config.routes.manageClubs}/${props.id}/events/applications`,
            title: "Applications",
            value: "applications",
        },
        {
            to: `${Config.routes.manageClubs}/${props.id}/events/past`,
            title: "Past",
            value: "past",
        },
    ];

    const selectedType =
        tabs.filter(tab => tab.to === history.location.pathname)[0]?.value ??
        "upcoming";

    React.useEffect((): any => {
        let cancel = false;
        if (selectedType === "upcoming" || selectedType === "past") {
            NProgress.start();
            setClubEvents([]);
            setIsLoading(true);
            API.userGetMyClubEvents({
                id: props.id,
                page: params.page_num,
                perPage: POSTS_PER_PAGE,
                type: selectedType,
            })
                .then(
                    ({
                        data,
                        headers,
                    }: {
                        data: Array<any>;
                        headers: Object;
                    }) => {
                        if (!cancel) {
                            setClubEvents(data);
                            setHeaders(headers);
                        }
                    }
                )
                .finally(() => {
                    setIsLoading(false);
                    NProgress.done();
                    scrollToTop();
                });
        }
    }, [params, selectedType]); // eslint-disable-line

    const scrollToTop = () => {
        // if (containerRef.current) {
        //     scrollToEl(containerRef.current);
        // }

        window.scrollTo({
            top: 0,
            left: 0,
            // behavior: "smooth",
        });
    };

    return (
        <div className="row manage-clubs-events-body">
            <div className="col-xs-12">
                <div className="row">
                    <div className="col-xs-12">
                        <div>
                            <h2 className="heading-1-v2">Events</h2>

                            <p>
                                All your approved club events will appear here,
                                including events where your club is a co-host.
                                Any events awaiting approval will be listed here
                                only once approved.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12">
                        <NavTabs
                            tabs={tabs}
                            className="manage-club-events"
                            cta={{
                                url: create_event_form_link
                                    ? create_event_form_link.url
                                    : "",
                                target: create_event_form_link
                                    ? create_event_form_link.target
                                    : "",
                                title: "Apply for a new event",
                            }}
                        />
                    </div>
                </div>
                <div className="row manage-clubs-events-body__event-list">
                    <div className="col-xs-12">
                        <Switch>
                            <Route
                                exact
                                path={`${Config.routes.manageClubs}/${props.id}/events`}
                                render={() => {
                                    return (
                                        <EventList
                                            events={clubEvents}
                                            type="upcoming"
                                            id={props.id}
                                            isLoading={isLoading}
                                        />
                                    );
                                }}
                            />
                            <Route
                                exact
                                path={`${Config.routes.manageClubs}/${props.id}/events/applications`}
                                render={() => {
                                    return <ApplicationList id={props.id} />;
                                }}
                            />
                            <Route
                                exact
                                path={`${Config.routes.manageClubs}/${props.id}/events/past`}
                                render={() => {
                                    return (
                                        <EventList
                                            events={clubEvents}
                                            type="past"
                                            id={props.id}
                                            isLoading={isLoading}
                                        />
                                    );
                                }}
                            />
                        </Switch>

                        {(selectedType === "upcoming" ||
                            selectedType === "past") &&
                        totalPages > 1 ? (
                            <Pagination
                                ellipsisWidth={PAGINATION_WIDTH}
                                pages={totalPages}
                                activePage={activePage}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

ManageClubsEventsBody.preload = ({
    location,
    passed: { store },
}: any): Promise<any> => {
    return Promise.all([]);
};

export default withRouter(ManageClubsEventsBody);
